import { CancelReason, CancelSubReason, LearnWayReason, KeepLearningReason } from '@brighterly/lib-core-types/subscription/enums'

import { useSubscriptionCancelStore } from '@entities/subscription'
import { useToast } from '@shared/lib/toast'
import { useCsRoutes } from '@shared/api'

interface SubscriptionCancelApiOptions {
  onStart?: () => void
  onSuccess?: (description: string) => void | Promise<void>
  onError?: (description: string) => void
}

interface SubscriptionCancelApiData {
  reason: CancelReason
  sub_reason: CancelSubReason | LearnWayReason | KeepLearningReason | null
  message?: string
  duration?: string
  change_tutor: boolean
}

export const useSubscriptionCancelApi = async (options?: SubscriptionCancelApiOptions): Promise<void> => {
  const { $axios } = useNuxtApp()

  const subscriptionCancelStore = useSubscriptionCancelStore()
  const toast = useToast()

  const setError = (description: string): void => {
    console.error(description)
    options?.onError?.(description)
    toast.error(description)
  }

  const id = subscriptionCancelStore.getSubscription?.id || null

  if (!id) {
    setError('Unexpected error. Invalid subscription ID.')
  } else {
    const url = useCsRoutes('subscription.cancel', {
      '[subscription_id]': id,
    })
    if (!url) {
      setError('Unexpected error. Invalid request URL.')
    } else {
      subscriptionCancelStore.setLoading(true)

      const requestData: SubscriptionCancelApiData = {
        reason: subscriptionCancelStore.getCancelReason!,
        sub_reason: subscriptionCancelStore.getLearnWayReason || subscriptionCancelStore.getKeepLearningReason,
        change_tutor: false,
      }

      if (subscriptionCancelStore.isCancelReasonDescription) {
        requestData.message = subscriptionCancelStore.getCancelReasonDescription!
      }
      options?.onStart?.()

      try {
        const { data } = await $axios.delete(url, {
          data: requestData,
        })

        if (!data.success) {
          setError('Unexpected error. Request was failed.')
        } else {
          await options?.onSuccess?.(data.message)
        }
      } catch (err: any) {
        setError(err.message || 'Unexpected error')
      } finally {
        subscriptionCancelStore.setLoading(false)
      }
    }
  }
}
